import * as React from 'react';
import CreatableSelect from 'react-select/creatable';
import { CreditCard } from 'lucide-react';
import { ResponderFormularioContext } from '../../contexts/ResponderFormularioContext';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import ExclamationCircleIcon from '../../icons/ExclamationCircleIcon';
import { IOpcaoDeCabecalho } from '../../interfaces/IResponderFormularioResponse';
import { HttpService } from '../../../../Shared/HttpService';

const { useContext, useState, useEffect, useCallback } = React;

interface IProps {
    type: string;
    id: string;
    rodape?: boolean;
    camposMultiplaEscolha: IOpcaoDeCabecalho[];
    permitirEditar: boolean;
    modeloDeCadastroId: number | null;
    modelosDeCadastro: any;
    modeloDeCadastroColunaId?: number | null;
}

interface IRegistroPrecadastrado {
    agrupamentoId: number;
    modeloCadastroId: number;
    campos: ICampoRegistro[];
}

interface ICampoRegistro {
    colunaId: number;
    colunaNome: string;
    valor: string;
    agrupamentoid?: number;
    modeloCadastroid?: number;
    tipo?: number;
}

interface ISelectOption {
    label: string;
    value: string;
    registro?: IRegistroPrecadastrado;
    outrosCampos?: { nome: string; valor: string }[];
}

const CampoDeCabecalho = ({ type, id, rodape, camposMultiplaEscolha, permitirEditar, modelosDeCadastro, modeloDeCadastroColunaId }: IProps) => {
    const cabecalhoOuRodape = rodape ? 'rodapes' : 'cabecalhos';
    const identificadorDoModeloDeCadastro = modelosDeCadastro ? modelosDeCadastro.modeloCadastroColunaIdentificadorId : null;
    const esteCabecalhoEhIdentificador = modeloDeCadastroColunaId === identificadorDoModeloDeCadastro && identificadorDoModeloDeCadastro !== null;
    const [classeDeFeedbackDePreenchimento, setClasseDeFeedbackDePreenchimento] = useState('');
    const [opcoesPrecadastradas, setOpcoesPrecadastradas] = useState<ISelectOption[]>([]);
    const [carregandoOpcoes, setCarregandoOpcoes] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [temMuitosRegistros, setTemMuitosRegistros] = useState(false);
    const { responderFormulario, setResponderFormulario, ultimaTentativaDeFinalizar } = useContext(ResponderFormularioContext);
    let tipo = "";

    switch (type) {
        case '1':
            tipo = "text";
            break;
        case '2':
            tipo = "date";
            break;
        case '3':
            tipo = "number";
            break;
        case '4':
            tipo = "time";
            break;
        case '5':
            tipo = "select";
            break;
        default:
            tipo = "help";
            break;
    }

    const responderFormularioAtualizado = { ...responderFormulario };
    const cabecalhoOuRodapeAtual = responderFormularioAtualizado[cabecalhoOuRodape]
        .find(itemDeCabecalhoOuRodape => itemDeCabecalhoOuRodape.id.toString() === id);

    const handleInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (cabecalhoOuRodapeAtual) {
            cabecalhoOuRodapeAtual.valor = value;
            event.target.classList.remove('is-invalid');
            setClasseDeFeedbackDePreenchimento('');
            setResponderFormulario({ ...responderFormularioAtualizado });
        }
    };

    const handleSelectValue = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.target;
        if (cabecalhoOuRodapeAtual) {
            cabecalhoOuRodapeAtual.valor = value;
            event.target.classList.remove('is-invalid');
            setClasseDeFeedbackDePreenchimento('');
            setResponderFormulario({ ...responderFormularioAtualizado });
        }
    };

    // Validações por tipo
    const validarValorPorTipo = (valor: string, tipoInput: string): { valido: boolean; mensagem?: string } => {
        if (!valor || valor.trim() === '') {
            return { valido: true }; // Vazio é válido (obrigatoriedade é tratada separadamente)
        }

        switch (tipoInput) {
            case 'date':
                // Valida formato YYYY-MM-DD e data válida
                const regexData = /^\d{4}-\d{2}-\d{2}$/;
                if (!regexData.test(valor)) {
                    return { valido: false, mensagem: 'Formato de data inválido. Use AAAA-MM-DD' };
                }
                const data = new Date(valor);
                if (isNaN(data.getTime())) {
                    return { valido: false, mensagem: 'Data inválida' };
                }
                return { valido: true };

            case 'number':
                // Valida se é um número
                if (isNaN(Number(valor))) {
                    return { valido: false, mensagem: 'Digite apenas números' };
                }
                return { valido: true };

            case 'time':
                // Valida formato HH:MM
                const regexHora = /^([01]\d|2[0-3]):([0-5]\d)$/;
                if (!regexHora.test(valor)) {
                    return { valido: false, mensagem: 'Formato de hora inválido. Use HH:MM' };
                }
                return { valido: true };

            case 'text':
            default:
                return { valido: true };
        }
    };

    // Buscar registros pré-cadastrados
    const buscarRegistrosPrecadastrados = useCallback(async (termo: string = '') => {
        if (!modelosDeCadastro || !modeloDeCadastroColunaId) return;

        setCarregandoOpcoes(true);
        try {
            const response = await HttpService.fetch('api/ModeloCadastroVisualizarDadosApi/ListarSearch', {
                method: 'POST',
                body: JSON.stringify({
                    modeloCadastroId: modelosDeCadastro.id,
                    colunaIdentificadorId: modeloDeCadastroColunaId,
                    termo: termo
                })
            });

            if (response.ok) {
                const campos: ICampoRegistro[] = await response.json();

                // Agrupar campos por agrupamentoid
                const registrosAgrupados = campos.reduce((acc, campo) => {
                    const agrupamentoId = campo.agrupamentoid || 0;
                    if (!acc[agrupamentoId]) {
                        acc[agrupamentoId] = {
                            agrupamentoId: agrupamentoId,
                            modeloCadastroId: campo.modeloCadastroid || 0,
                            campos: []
                        };
                    }
                    acc[agrupamentoId].campos.push(campo);
                    return acc;
                }, {} as Record<number, IRegistroPrecadastrado>);

                // Converter para array de opções
                const opcoes: ISelectOption[] = Object.values(registrosAgrupados).map(registro => {
                    const campoIdentificador = registro.campos.find(c => c.colunaId === modeloDeCadastroColunaId);
                    const outrosCampos = registro.campos.filter(c => c.colunaId !== modeloDeCadastroColunaId);

                    return {
                        label: campoIdentificador?.valor || '',
                        value: campoIdentificador?.valor || '',
                        registro,
                        outrosCampos: outrosCampos.map(c => ({ nome: c.colunaNome, valor: c.valor }))
                    };
                });

                // Se tem 50 ou menos registros, não precisa fazer busca em tempo real
                setTemMuitosRegistros(opcoes.length > 50);
                setOpcoesPrecadastradas(opcoes);
            }
        } catch (error) {
            console.error('Erro ao buscar registros pré-cadastrados:', error);
        } finally {
            setCarregandoOpcoes(false);
        }
    }, [modelosDeCadastro, modeloDeCadastroColunaId]);

    // Preencher campos irmãos quando seleciona registro pré-cadastrado
    const preencherCamposIrmaos = (registro: IRegistroPrecadastrado) => {
        const cabecalhosOuRodapesIrmaos = responderFormularioAtualizado[cabecalhoOuRodape]
            .filter(item => item.modelodecadastroid === modelosDeCadastro?.id);

        registro.campos.forEach(campo => {
            const cabecalhoIrmao = cabecalhosOuRodapesIrmaos.find(
                item => item.modelodecadastrocolunaid === campo.colunaId
            );
            if (cabecalhoIrmao) {
                // Se for campo de múltipla escolha (select), verificar se o valor existe nas opções
                if (cabecalhoIrmao.tipoCabecalhoId === 5 && cabecalhoIrmao.camposCabecalhoMultiplaEscolhas) {
                    const opcaoExiste = cabecalhoIrmao.camposCabecalhoMultiplaEscolhas.some(
                        opcao => opcao.opcoesDeEcolha === campo.valor
                    );
                    // Só preenche se a opção existir
                    if (opcaoExiste) {
                        cabecalhoIrmao.valor = campo.valor;
                    }
                } else {
                    // Para outros tipos, preenche normalmente
                    cabecalhoIrmao.valor = campo.valor;
                }
            }
        });

        setResponderFormulario({ ...responderFormularioAtualizado });
    };

    // Handler do CreatableSelect
    const handleCreatableChange = (novaOpcao: any) => {
        if (!cabecalhoOuRodapeAtual) return;

        if (!novaOpcao) {
            // Se limpou o campo
            cabecalhoOuRodapeAtual.valor = '';
            setResponderFormulario({ ...responderFormularioAtualizado });
            return;
        }

        // Se selecionou uma opção pré-cadastrada, preenche todos os campos
        if (novaOpcao.registro) {
            preencherCamposIrmaos(novaOpcao.registro);
        } else {
            // Se criou um novo valor, apenas preenche o campo atual
            cabecalhoOuRodapeAtual.valor = novaOpcao.value;
            setResponderFormulario({ ...responderFormularioAtualizado });
        }

        setClasseDeFeedbackDePreenchimento('');
    };

    // Handler para criar nova opção com validação
    const handleCreateOption = (inputValue: string) => {
        const validacao = validarValorPorTipo(inputValue, tipo);

        if (!validacao.valido) {
            alert(validacao.mensagem);
            return;
        }

        if (cabecalhoOuRodapeAtual) {
            cabecalhoOuRodapeAtual.valor = inputValue;
            setResponderFormulario({ ...responderFormularioAtualizado });
            setClasseDeFeedbackDePreenchimento('');
        }
    };

    // Busca com debounce conforme usuário digita (só se tiver mais de 50 registros)
    useEffect(() => {
        if (!esteCabecalhoEhIdentificador || !permitirEditar || !temMuitosRegistros) return;

        const timeoutId = setTimeout(() => {
            buscarRegistrosPrecadastrados(inputValue);
        }, 1000); // 1 segundo de debounce

        return () => clearTimeout(timeoutId);
    }, [inputValue, esteCabecalhoEhIdentificador, permitirEditar, temMuitosRegistros, buscarRegistrosPrecadastrados]);

    // Carregar opções iniciais ao montar
    useEffect(() => {
        if (esteCabecalhoEhIdentificador && permitirEditar) {
            buscarRegistrosPrecadastrados();
        }
    }, [esteCabecalhoEhIdentificador, permitirEditar, buscarRegistrosPrecadastrados]);

    useDidUpdateEffect(() => {
        const cabecalhoEstaPreenchido = (cabecalhoOuRodapeAtual && cabecalhoOuRodapeAtual.valor && cabecalhoOuRodapeAtual.valor.length === 0) || !cabecalhoOuRodapeAtual?.valor;
        if (cabecalhoEstaPreenchido && cabecalhoOuRodapeAtual?.cabecalhoObrigatorio) {
            setClasseDeFeedbackDePreenchimento('is-invalid');
        }
    }, [ultimaTentativaDeFinalizar]);

    if (!cabecalhoOuRodapeAtual) return <React.Fragment />;

    const opcoesCabecalho = (camposMultiplaEscolha).map((opcao, i) =>
        <option value={opcao.opcoesDeEcolha}>{opcao.opcoesDeEcolha}</option>
    );

    if (permitirEditar) return (
        <div className="form-group col-sm-6">
            <label
                htmlFor={id}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
                <span>
                    {
                        cabecalhoOuRodapeAtual.cabecalhoObrigatorio
                            ? <strong>{cabecalhoOuRodapeAtual.titulo}</strong>
                            : cabecalhoOuRodapeAtual.titulo
                    }
                </span>
                {
                    esteCabecalhoEhIdentificador &&
                    <span
                        title="Campo Identificador - Preenche outros campos automaticamente"
                        style={{
                            cursor: 'help',
                            display: 'inline-flex',
                            alignItems: 'center'
                        }}
                    >
                        <CreditCard size={16} color="#3498db" />
                    </span>
                }
                {
                    (!cabecalhoOuRodapeAtual.valor || cabecalhoOuRodapeAtual.valor.length === 0) && cabecalhoOuRodapeAtual.cabecalhoObrigatorio
                        ? <ExclamationCircleIcon fill='#ef0000' className='ml-2 mb-2' />
                        : <React.Fragment />
                }
            </label>
            {
                tipo === "help"
                    ? <div>{cabecalhoOuRodapeAtual.ajuda}</div>
                    : tipo === "select"
                        ? esteCabecalhoEhIdentificador
                            ?   // SELECT normal não permite criar novos valores quando é identificador
                            <select
                                className={`form-control ${classeDeFeedbackDePreenchimento}`}
                                onChange={handleSelectValue}
                                value={cabecalhoOuRodapeAtual.valor ? cabecalhoOuRodapeAtual.valor : ''}
                            >
                                <option value="">Selecione</option>
                                {opcoesCabecalho}
                            </select>
                            : <select
                                className="form-control"
                                onChange={handleSelectValue}
                                value={cabecalhoOuRodapeAtual.valor ? cabecalhoOuRodapeAtual.valor : ''}
                            >
                                <option value="">Selecione</option>
                                {opcoesCabecalho}
                            </select>
                        : esteCabecalhoEhIdentificador
                            ?   // CreatableSelect para campos identificadores (exceto help e select)
                            <CreatableSelect
                                isClearable
                                isLoading={carregandoOpcoes}
                                options={opcoesPrecadastradas}
                                onChange={handleCreatableChange}
                                onCreateOption={handleCreateOption}
                                onInputChange={(newValue, actionMeta) => {
                                    if (actionMeta.action === 'input-change') {
                                        setInputValue(newValue);
                                    }
                                }}
                                value={
                                    cabecalhoOuRodapeAtual.valor
                                        ? { label: cabecalhoOuRodapeAtual.valor, value: cabecalhoOuRodapeAtual.valor }
                                        : null
                                }
                                placeholder="Digite para buscar ou criar novo..."
                                formatCreateLabel={(inputValue) => `Criar novo: "${inputValue}"`}
                                formatOptionLabel={(option: ISelectOption) => (
                                    <div style={{ padding: '4px 0' }}>
                                        <div style={{
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            color: '#2c3e50',
                                            marginBottom: '4px'
                                        }}>
                                            {option.label}
                                        </div>
                                        {option.outrosCampos && option.outrosCampos.length > 0 && (
                                            <div style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '8px',
                                                marginTop: '4px'
                                            }}>
                                                {option.outrosCampos.map((campo, index) => (
                                                    <span
                                                        key={index}
                                                        style={{
                                                            fontSize: '12px',
                                                            color: '#7f8c8d',
                                                            backgroundColor: '#ecf0f1',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px'
                                                        }}
                                                    >
                                                        <strong style={{ color: '#34495e' }}>{campo.nome}:</strong> {campo.valor}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                noOptionsMessage={({ inputValue }) =>
                                    inputValue ? "Nenhum registro encontrado" : "Digite para buscar..."
                                }
                                loadingMessage={() => "Buscando..."}
                                className={classeDeFeedbackDePreenchimento}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderColor: classeDeFeedbackDePreenchimento === 'is-invalid' ? '#dc3545' : base.borderColor,
                                        minHeight: '38px'
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isFocused ? '#e3f2fd' : state.isSelected ? '#2196f3' : 'white',
                                        color: state.isSelected ? 'white' : '#2c3e50',
                                        padding: '10px 12px',
                                        cursor: 'pointer',
                                        ':active': {
                                            backgroundColor: '#bbdefb'
                                        }
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999
                                    })
                                }}
                            />
                            : <input
                                type={tipo}
                                className={`form-control ${classeDeFeedbackDePreenchimento}`}
                                id={id}
                                name={id}
                                value={cabecalhoOuRodapeAtual.valor ? cabecalhoOuRodapeAtual.valor : ''}
                                onChange={handleInputValue}
                                {...tipo === 'date' ? { max: '9999-12-31' } : {}}
                            />
            }
        </div>
    );

    return (
        <div className="form-group col-sm-6">
            <div className='shadow-sm d-flex flex-column justify-content-start align-items-center px-3 py-2 border rounded h-100' style={{ backgroundColor: '#e9f4f7' }}>
                <label className='text-muted text-break'>{cabecalhoOuRodapeAtual.titulo}</label>
                <div className='w-100 text-break'>{tipo === 'help' ? cabecalhoOuRodapeAtual.ajuda : cabecalhoOuRodapeAtual.valor}</div>
            </div>
        </div>
    );
}

export default CampoDeCabecalho;
